import type { CalendarMode } from "../../../shared/types";
import { LockClosedIcon, LockOpenIcon } from "../../../ui/icons";
import { getMonthYear, getDayName, getDayNumber, getToday } from "../../../shared/types/date-helpers";
import { MONTH_NAMES } from "../../../shared/types/date-helpers";
import { MonthYearPicker } from "./MonthYearPicker";
import { CalendarHelpButton } from "./week-grid/WeekGridDayHeaders";

type NavigationVariant = "default" | "google";

interface CalendarNavigationProps {
  readonly currentDate: string;
  readonly selectedDate?: string | null;
  readonly calendarMode: CalendarMode;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onToday: () => void;
  readonly onGoToDate: (date: string) => void;
  readonly onModeChange: (mode: CalendarMode) => void;
  readonly variant?: NavigationVariant;
  readonly readOnly?: boolean;
  readonly onToggleReadOnly?: () => void;
  readonly onToggleFullscreen?: () => void;
  readonly onToggleCompact?: () => void;
}

// Mobile: Jour, Semaine et Mois
const MOBILE_MODES: { mode: CalendarMode; label: string; shortLabel: string }[] = [
  { mode: "day", label: "Jour", shortLabel: "J" },
  { mode: "week", label: "Semaine", shortLabel: "S" },
  { mode: "month", label: "Mois", shortLabel: "M" },
];

// Desktop: Jour, Semaine, Mois
const DESKTOP_MODES: { mode: CalendarMode; label: string; shortLabel: string }[] = [
  { mode: "day", label: "Jour", shortLabel: "J" },
  { mode: "week", label: "Semaine", shortLabel: "S" },
  { mode: "month", label: "Mois", shortLabel: "M" },
];

const PREV_LABELS: Record<CalendarMode, string> = {
  day: "Jour précédent",
  week: "Semaine précédente",
  month: "Mois précédent",
};

const NEXT_LABELS: Record<CalendarMode, string> = {
  day: "Jour suivant",
  week: "Semaine suivante",
  month: "Mois suivant",
};

function formatDayName(dateStr: string, todayStr: string): string {
  if (!dateStr) {
    return "Aujourd'hui";
  }

  if (dateStr === todayStr) {
    return "Aujourd'hui";
  }

  try {
    const today = new Date(todayStr + "T00:00:00");
    const date = new Date(dateStr + "T00:00:00");

    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Demain";
    }
    if (diffDays === -1) {
      return "Hier";
    }

    const dayName = getDayName(dateStr);
    const dayNum = getDayNumber(dateStr);
    const year = date.getFullYear();

    if (!dayName || isNaN(dayNum)) return dateStr;

    const monthName = MONTH_NAMES[date.getMonth()];
    return `${dayName.toUpperCase()} ${dayNum} ${monthName.toUpperCase()} ${year}`;
  } catch {
    return dateStr;
  }
}

export function CalendarNavigation({
  currentDate,
  selectedDate,
  calendarMode,
  onPrev,
  onNext,
  onToday,
  onGoToDate,
  onModeChange,
  variant = "default",
  readOnly,
  onToggleReadOnly,
  onToggleFullscreen,
  onToggleCompact,
}: CalendarNavigationProps) {
  const todayStr = getToday();
  const isGoogle = variant === "google";

  // Style Google Calendar : header horizontal compact
  if (isGoogle) {
    return (
      <div className="flex flex-col gap-1 px-3 py-2 border-b border-border/15 bg-surface/80 backdrop-blur-sm
        md:flex-row md:items-center md:gap-4 md:px-4">
        {/* Row 1: Nav arrows + title + add button (mobile) / full row (desktop) */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Today button - hidden on mobile, shown on desktop */}
          <button
            type="button"
            onClick={onToday}
            className="hidden md:flex px-4 py-1.5 text-sm font-medium rounded-md border border-border/40
              text-ink hover:bg-surface-alt active:bg-surface-alt/80
              focus:outline-none focus:ring-2 focus:ring-accent/40 transition-colors shrink-0"
          >
            Aujourd&apos;hui
          </button>

          {/* Prev / Next arrows */}
          <div className="flex items-center shrink-0">
            <button
              type="button"
              onClick={onPrev}
              aria-label={PREV_LABELS[calendarMode]}
              className="w-8 h-8 flex items-center justify-center rounded-full
                text-ink-muted hover:bg-surface-alt active:bg-surface-alt/80
                focus:outline-none transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onNext}
              aria-label={NEXT_LABELS[calendarMode]}
              className="w-8 h-8 flex items-center justify-center rounded-full
                text-ink-muted hover:bg-surface-alt active:bg-surface-alt/80
                focus:outline-none transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Month/Year title */}
          <MonthYearPicker
            currentDate={currentDate}
            onGoToDate={onGoToDate}
            label={getMonthYear(currentDate)}
            className="text-base md:text-xl font-medium md:font-normal text-ink hover:text-accent transition-colors cursor-pointer select-none truncate"
          />

          {/* Mobile spacer + buttons */}
          <div className="flex-1 md:hidden" />

          {/* Mobile today button (compact) */}
          <button
            type="button"
            onClick={onToday}
            className="md:hidden px-2.5 py-1 text-xs font-medium rounded-md border border-border/40
              text-ink hover:bg-surface-alt active:bg-surface-alt/80
              focus:outline-none transition-colors shrink-0"
          >
            Auj.
          </button>
          {onToggleReadOnly && (
            <button
              type="button"
              onClick={onToggleReadOnly}
              aria-label={readOnly ? "Déverrouiller" : "Verrouiller"}
              className={`md:hidden w-8 h-8 flex items-center justify-center rounded-md transition-colors shrink-0 ${
                readOnly
                  ? "text-accent bg-accent/10"
                  : "text-ink-muted hover:bg-surface-alt"
              }`}
            >
              {readOnly ? <LockClosedIcon className="w-4 h-4" /> : <LockOpenIcon className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Desktop spacer */}
        <div className="hidden md:block flex-1" />

        {/* View mode tabs */}
        <div className="flex items-center bg-surface-alt/60 rounded-lg p-0.5 self-start md:self-auto shrink-0">
          {DESKTOP_MODES.map(({ mode, label, shortLabel }) => (
            <button
              key={mode}
              type="button"
              onClick={() => onModeChange(mode)}
              className={`text-xs md:text-sm px-2.5 md:px-3.5 py-1 md:py-1.5 font-medium rounded-md transition-all ${
                calendarMode === mode
                  ? "bg-surface text-ink shadow-sm"
                  : "text-ink-muted hover:text-ink"
              } focus:outline-none`}
            >
              <span className="md:hidden">{shortLabel}</span>
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Style par défaut : layout vertical compact
  const effectiveDate = selectedDate ?? currentDate;

  return (
    <div className="flex flex-col gap-2 md:gap-3">
      {/* Desktop : bouton Aujourd'hui */}
      <button
        type="button"
        onClick={onToday}
        className="hidden md:block w-full px-3 py-2 text-sm font-medium text-accent hover:bg-accent/10 rounded-lg transition-colors text-center"
      >
        {formatDayName(effectiveDate, todayStr)}
      </button>

      {/* Navigation mois — mobile : fusionné avec Aujourd'hui + lock + aide */}
      <div className="flex items-center justify-between bg-surface-alt/50 rounded-lg p-1">
        {/* Mobile : bouton Aujourd'hui compact */}
        <button
          type="button"
          onClick={onToday}
          className="md:hidden text-[10px] font-semibold text-accent px-1.5 py-1 rounded-md hover:bg-accent/10 active:bg-accent/15 transition-colors shrink-0"
        >
          {formatDayName(effectiveDate, todayStr)}
        </button>
        <button
          type="button"
          onClick={onPrev}
          aria-label={PREV_LABELS[calendarMode]}
          className="min-h-8 min-w-8 flex items-center justify-center rounded-md
            text-ink-muted hover:text-ink hover:bg-surface transition-colors focus:outline-none focus:ring-1 focus:ring-accent shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div className="flex-1 text-center">
          <MonthYearPicker
            currentDate={currentDate}
            onGoToDate={onGoToDate}
            label={getMonthYear(currentDate)}
            className="inline-block text-sm md:text-base font-semibold text-ink hover:text-accent transition-colors cursor-pointer px-2 py-1"
          />
        </div>
        <button
          type="button"
          onClick={onNext}
          aria-label={NEXT_LABELS[calendarMode]}
          className="min-h-8 min-w-8 flex items-center justify-center rounded-md
            text-ink-muted hover:text-ink hover:bg-surface transition-colors focus:outline-none focus:ring-1 focus:ring-accent shrink-0"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        {/* Mobile : boutons compacts lock + fullscreen + aide */}
        <div className="flex items-center gap-0.5 md:hidden">
          {onToggleFullscreen && (
            <button
              type="button"
              onClick={onToggleFullscreen}
              aria-label="Plein écran"
              className="w-7 h-7 flex items-center justify-center rounded text-ink-faint hover:text-ink-muted hover:bg-surface active:bg-surface/70 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m7-5h4m0 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m7 5h4m0 0v-4m0 4l-5-5" />
              </svg>
            </button>
          )}
          {onToggleReadOnly && (
            <button
              type="button"
              onClick={onToggleReadOnly}
              aria-label={readOnly ? "Déverrouiller" : "Verrouiller"}
              className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                readOnly
                  ? "text-accent"
                  : "text-ink-faint hover:text-ink-muted hover:bg-surface active:bg-surface/70"
              }`}
            >
              {readOnly ? <LockClosedIcon className="w-3.5 h-3.5" /> : <LockOpenIcon className="w-3.5 h-3.5" />}
            </button>
          )}
          <CalendarHelpButton />
        </div>
      </div>

      {/* Modes - affiche les bons modes selon desktop/mobile via CSS */}
      <div className="flex gap-1 bg-surface-alt/50 rounded-lg p-1">
        {/* Mobile: Jour et Mois uniquement */}
        {MOBILE_MODES.map(({ mode, label }) => (
          <button
            key={mode}
            type="button"
            onClick={() => onModeChange(mode)}
            className={`md:hidden flex-1 text-xs px-3 py-1.5 font-medium rounded-md transition-all text-center ${
              calendarMode === mode
                ? "bg-surface text-ink shadow-sm"
                : "text-ink-muted hover:text-ink hover:bg-surface/50"
            } focus:outline-none focus:ring-1 focus:ring-accent`}
          >
            {label}
          </button>
        ))}
        {/* Desktop: Jour, Semaine, Mois */}
        {DESKTOP_MODES.map(({ mode, label }) => (
          <button
            key={mode}
            type="button"
            onClick={() => onModeChange(mode)}
            className={`hidden md:flex flex-1 text-sm px-3 py-1.5 font-medium rounded-md transition-all text-center ${
              calendarMode === mode
                ? "bg-surface text-ink shadow-sm"
                : "text-ink-muted hover:text-ink hover:bg-surface/50"
            } focus:outline-none focus:ring-1 focus:ring-accent`}
          >
            {label}
          </button>
        ))}
        {onToggleCompact && (
          <button
            type="button"
            onClick={onToggleCompact}
            className="md:hidden w-7 h-7 flex items-center justify-center rounded-md text-accent/40 active:text-accent transition-colors shrink-0"
            aria-label="Masquer la navigation"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
